/**
 * useConfirmModal - Confirmation par fenêtre modale, en remplacement de
 * `window.confirm()` (interdit dans l'application).
 *
 * API :
 *   const { ask } = useConfirmModal()
 *   if (!(await ask({ title, message, confirmLabel?, cancelLabel?, severity?, icon? }))) return
 *
 * `ask()` renvoie une promesse résolue à `true` (confirmé) ou `false`
 * (annulé / fermé). Le composant <AppConfirm /> doit être monté une fois
 * dans App.vue pour afficher la modale.
 */
import { reactive } from 'vue'

const state = reactive({
  open: false,
  title: 'Confirmer',
  message: 'Confirmez-vous cette action ?',
  confirmLabel: 'Confirmer',
  cancelLabel: 'Annuler',
  severity: 'danger',
  icon: '',
})

let pendingResolve = null

function settle(result) {
  const resolve = pendingResolve
  pendingResolve = null
  state.open = false
  resolve?.(result)
}

function ask(options = {}) {
  // Une demande encore ouverte est considérée comme annulée.
  if (pendingResolve) settle(false)
  Object.assign(state, {
    title: options.title || 'Confirmer',
    message: options.message || 'Confirmez-vous cette action ?',
    confirmLabel: options.confirmLabel || 'Confirmer',
    cancelLabel: options.cancelLabel || 'Annuler',
    severity: options.severity || 'danger',
    icon: options.icon || '',
  })
  state.open = true
  return new Promise((resolve) => {
    pendingResolve = resolve
  })
}

export function useConfirmModal() {
  return {
    state,
    ask,
    confirm: () => settle(true),
    cancel: () => settle(false),
  }
}
